import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, CalendarRange, type LucideIcon } from 'lucide-react';
import {
  FilterFieldItem,
  type FilterField,
  type FilterOption,
} from '@/components/common/AdvancedFilter';
import { DetailPanel } from '@/components/common/DetailPanel';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCard, StatCardSkeleton } from '@/components/common/StatCard';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import type { LeaveType } from '@/features/leaveTypes/types/leaveType.types';
import { leaveTypesQueryOptions } from '@/features/leaveTypes/api/leaveTypes.api';
import { cn } from '@/lib/utils';
import { REFETCH_FADE_TRANSITION_CLASS, REFETCHING_OPACITY_CLASS } from '@/styles/refetchFade';
import { getMonthOptions, getYearOptions } from '@/utils/date';
import { parseNumberFilter, toFilterOptions } from '@/utils/filters';
import { pluralize } from '@/utils/number';
import { leaveUsageSummaryQueryOptions } from '../api/leaveApplications.api';
import type {
  LeaveUsage,
  LeaveUsageSummary as LeaveUsageSummaryData,
} from '../types/leaveApplication.types';
import { getLeaveUsageCounts } from '../utils/leaveUsageCounts';

const STAT_CARD_DENSITY = 'compact';
const SKELETON_CARD_KEYS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'] as const;
const ZERO_COUNT_CARD_CLASS = 'opacity-50';
const USAGE_GRID_CLASS = 'grid grid-cols-2 gap-2.5 sm:grid-cols-3';
const YEARS_BEFORE_THIS_YEAR = 5;
const YEARS_AFTER_THIS_YEAR = 1;
const NO_LEAVE_TYPES: LeaveType[] = [];
const MONTH_OPTIONS = toFilterOptions(getMonthOptions());

type UsagePeriod = 'year' | 'month';

interface UsagePeriodConfig {
  title: string;
  icon: LucideIcon;
  hasMonthPicker: boolean;
  getUsage: (summary: LeaveUsageSummaryData) => LeaveUsage;
}

const USAGE_PERIOD_CONFIG: Record<UsagePeriod, UsagePeriodConfig> = {
  year: {
    title: 'Yearly Summary',
    icon: CalendarRange,
    hasMonthPicker: false,
    getUsage: (summary) => summary.yearly,
  },
  month: {
    title: 'Monthly Summary',
    icon: CalendarDays,
    hasMonthPicker: true,
    getUsage: (summary) => summary.monthly,
  },
};

function getUsageYearOptions(shownYear: number | undefined) {
  const thisYear = new Date().getFullYear();
  const firstYear = Math.min(thisYear - YEARS_BEFORE_THIS_YEAR, shownYear ?? thisYear);
  const lastYear = Math.max(thisYear + YEARS_AFTER_THIS_YEAR, shownYear ?? thisYear);
  return getYearOptions(firstYear, lastYear);
}

interface PeriodPickerProps {
  fieldKey: string;
  label: string;
  options: FilterOption[];
  value: number | undefined;
  onChange: (value: number) => void;
  widthClassName: string;
}

function PeriodPicker({
  fieldKey,
  label,
  options,
  value,
  onChange,
  widthClassName,
}: PeriodPickerProps) {
  const isLoading = value === undefined;
  const field: FilterField = {
    key: fieldKey,
    label,
    type: 'select',
    options,
    isClearable: false,
    isCompact: true,
    ...(isLoading ? { placeholder: LOADING_PLACEHOLDER, disabled: true } : {}),
  };

  function handleChange(values: Record<string, string>) {
    const pickedValue = parseNumberFilter(values[fieldKey]);
    if (pickedValue !== undefined) onChange(pickedValue);
  }

  return (
    <div className={widthClassName}>
      <FilterFieldItem
        field={field}
        values={{ [fieldKey]: isLoading ? '' : String(value) }}
        onChange={handleChange}
      />
    </div>
  );
}

interface UsageCardsProps {
  usage: LeaveUsage | undefined;
  leaveTypes: LeaveType[] | undefined;
  isRefreshing: boolean;
}

function UsageCards({ usage, leaveTypes, isRefreshing }: UsageCardsProps) {
  const counts = useMemo(
    () => (leaveTypes ? getLeaveUsageCounts(usage ? [usage] : [], leaveTypes) : undefined),
    [usage, leaveTypes],
  );

  if (!counts) {
    return (
      <div className={USAGE_GRID_CLASS}>
        {SKELETON_CARD_KEYS.map((key) => (
          <StatCardSkeleton key={key} density={STAT_CARD_DENSITY} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        USAGE_GRID_CLASS,
        REFETCH_FADE_TRANSITION_CLASS,
        isRefreshing && REFETCHING_OPACITY_CLASS,
      )}
    >
      {counts.map((count) => {
        const value = usage ? count.getCount(usage) : undefined;
        return (
          <StatCard
            key={count.key}
            density={STAT_CARD_DENSITY}
            label={count.label}
            value={value}
            unit={value === undefined ? undefined : pluralize(value, count.unit)}
            isLoading={usage === undefined}
            className={value === 0 ? ZERO_COUNT_CARD_CLASS : undefined}
          />
        );
      })}
    </div>
  );
}

interface LeaveUsagePeriodPanelProps {
  applicationId: number;
  period: UsagePeriod;
  leaveTypes: LeaveType[] | undefined;
}

function LeaveUsagePeriodPanel({ applicationId, period, leaveTypes }: LeaveUsagePeriodPanelProps) {
  const config = USAGE_PERIOD_CONFIG[period];
  const [pickedYear, setPickedYear] = useState<number | undefined>();
  const [pickedMonth, setPickedMonth] = useState<number | undefined>();
  const params = useMemo(
    () => (config.hasMonthPicker ? { year: pickedYear, month: pickedMonth } : { year: pickedYear }),
    [config.hasMonthPicker, pickedYear, pickedMonth],
  );

  const {
    data: summary,
    error,
    isFetching,
    isPlaceholderData,
    refetch,
  } = useQuery(leaveUsageSummaryQueryOptions(applicationId, params));

  const yearOptions = useMemo(
    () => toFilterOptions(getUsageYearOptions(summary?.year)),
    [summary?.year],
  );

  const pickers = (
    <div className="flex flex-wrap items-center gap-2">
      <PeriodPicker
        fieldKey="year"
        label="Year"
        options={yearOptions}
        value={pickedYear ?? summary?.year}
        onChange={setPickedYear}
        widthClassName="w-24"
      />
      {config.hasMonthPicker ? (
        <PeriodPicker
          fieldKey="month"
          label="Month"
          options={MONTH_OPTIONS}
          value={pickedMonth ?? summary?.month}
          onChange={setPickedMonth}
          widthClassName="w-34"
        />
      ) : null}
    </div>
  );

  return (
    <DetailPanel title={config.title} icon={config.icon} actions={pickers} hasTintedHeader>
      {error && !summary ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the leave usage"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          size="compact"
        />
      ) : (
        <UsageCards
          usage={summary ? config.getUsage(summary) : undefined}
          leaveTypes={leaveTypes}
          isRefreshing={isPlaceholderData}
        />
      )}
    </DetailPanel>
  );
}

interface LeaveUsageSummaryProps {
  applicationId: number;
}

export function LeaveUsageSummary({ applicationId }: LeaveUsageSummaryProps) {
  const { data: leaveTypes, isError: hasLeaveTypesError } = useQuery(leaveTypesQueryOptions());
  const shownLeaveTypes = leaveTypes ?? (hasLeaveTypesError ? NO_LEAVE_TYPES : undefined);

  return (
    <div className="@container">
      <div className="grid gap-4 @[66rem]:grid-cols-2">
        <LeaveUsagePeriodPanel
          applicationId={applicationId}
          period="year"
          leaveTypes={shownLeaveTypes}
        />
        <LeaveUsagePeriodPanel
          applicationId={applicationId}
          period="month"
          leaveTypes={shownLeaveTypes}
        />
      </div>
    </div>
  );
}
